import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';

function Stat({ label, value, hint }) {
  return (
    <div className="relative overflow-hidden rounded-[20px] border border-surface-container-highest bg-surface px-4 py-3.5 shadow-[0_12px_24px_rgba(15,23,42,0.04)]">
      <div className="absolute inset-x-0 top-0 h-0.5 bg-[linear-gradient(90deg,var(--color-primary)_0%,color-mix(in_srgb,var(--color-primary)_68%,white)_100%)]" />
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-on-surface-variant">{label}</p>
      <p className="mt-1.5 text-[18px] font-semibold leading-none tracking-[-0.04em] text-on-surface">{value}</p>
      {hint ? <p className="mt-1.5 text-[10px] leading-5 text-on-surface-variant">{hint}</p> : null}
    </div>
  );
}

function CollectionCard({ title, badge, empty, action, items, renderItem }) {
  return (
    <div className="relative overflow-hidden rounded-[22px] border border-surface-container-highest bg-surface px-4 py-4 shadow-[0_12px_24px_rgba(15,23,42,0.04)]">
      <div className="absolute left-4 top-0 h-1 w-20 rounded-b-full bg-primary" />
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="studio-uniform-title font-h3 text-h3 tracking-[-0.02em] text-on-surface">{title}</h3>
        </div>
        <div className="flex items-center gap-1.5">
          {badge ? (
            <span className="rounded-full border border-primary/10 bg-primary-container px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-on-primary-container">
              {badge}
            </span>
          ) : null}
          {action}
        </div>
      </div>
      {items.length === 0 ? (
        <p className="mt-3 text-[10px] leading-5 text-on-surface-variant">{empty}</p>
      ) : (
        <div className="mt-3 space-y-2">{items.map(renderItem)}</div>
      )}
    </div>
  );
}

function formatDate(value) {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.dashboard().then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) return <p className="text-[11px] text-error">{error}</p>;
  if (!data) return <p className="text-[11px] text-on-surface-variant">Carregando…</p>;

  const createdLabels = data.templates.recent.filter((item) => item.status === 'PUBLISHED');
  const draftLabels = data.templates.recent.filter((item) => item.status === 'DRAFT');

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-on-surface-variant">Visío geral</div>
          <h1 className="studio-uniform-title mt-1.5 font-h2 text-h2 tracking-[-0.04em] text-on-surface">Dashboard</h1>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <Link
            to="/formatos"
            className="rounded-[18px] border border-surface-container-high bg-surface px-3.5 py-2.5 text-[10px] font-semibold text-on-surface"
          >
            Novo formato
          </Link>
          <Link
            to="/templates"
            className="rounded-[18px] bg-primary px-3.5 py-2.5 text-[10px] font-semibold text-on-primary shadow-[0_10px_20px_rgba(249,115,22,0.18)]"
          >
            Nova etiqueta
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-3 xl:grid-cols-5">
        <Stat label="Usuários" value={data.users.total} hint={`Ativos: ${data.users.status.ACTIVE ?? 0}`} />
        <Stat label="Etiquetas" value={data.templates.total} hint={`Publicadas: ${data.templates.status.PUBLISHED ?? 0}`} />
        <Stat label="Em Criação" value={data.templates.status.DRAFT ?? 0} hint="Rascunhos prontos para edição" />
        <Stat label="Formatos" value={data.labelFormats.total} hint={`${Object.keys(data.labelFormats.byMediaType).length} tipos de mídia`} />
        <Stat label="Fila de impressão" value={data.printJobs.total} hint={`Em andamento: ${(data.printJobs.status.QUEUED ?? 0) + (data.printJobs.status.PRINTING ?? 0)}`} />
      </div>

      <div className="grid gap-3 xl:grid-cols-3">
        <CollectionCard
          title="Etiquetas criadas"
          badge={`${createdLabels.length} recentes`}
          empty="Nenhuma etiqueta publicada ainda."
          action={
            <Link to="/templates" className="text-[10px] font-semibold text-primary">
              Ver catálogo
            </Link>
          }
          items={createdLabels}
          renderItem={(item) => (
            <Link
              key={item.id}
              to={`/templates/${item.id}/editar`}
              className="block rounded-[18px] border border-surface-container-high bg-surface-container-low px-3.5 py-3 transition hover:border-primary/30 hover:bg-primary-container/10"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] font-semibold leading-5 text-on-surface">{item.name}</div>
                  <div className="mt-0.5 text-[10px] leading-5 text-on-surface-variant">{item.labelFormat?.name || 'Sem formato vinculado'}</div>
                </div>
                <span className="rounded-full bg-primary-container px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-on-primary-container">
                  Publicada
                </span>
              </div>
              <div className="mt-1.5 text-[10px] leading-5 text-on-surface-variant">Atualizada em {formatDate(item.updatedAt)}</div>
            </Link>
          )}
        />

        <CollectionCard
          title="Etiquetas a criar"
          badge={`${draftLabels.length} rascunhos`}
          empty="Nenhum rascunho pendente."
          action={
            <Link to="/templates" className="text-[10px] font-semibold text-primary">
              Editar
            </Link>
          }
          items={draftLabels}
          renderItem={(item) => (
            <Link
              key={item.id}
              to={`/templates/${item.id}/editar`}
              className="block rounded-[18px] border border-surface-container-high bg-surface-container-low px-3.5 py-3 transition hover:border-primary/30 hover:bg-primary-container/10"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] font-semibold leading-5 text-on-surface">{item.name}</div>
                  <div className="mt-0.5 text-[10px] leading-5 text-on-surface-variant">{item.labelFormat?.name || 'Defina um formato para concluir'}</div>
                </div>
                <span className="rounded-full bg-surface px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">
                  Rascunho
                </span>
              </div>
              <div className="mt-1.5 text-[10px] leading-5 text-on-surface-variant">Atualizada em {formatDate(item.updatedAt)}</div>
            </Link>
          )}
        />

        <CollectionCard
          title="Gestão de impressão"
          badge={`${data.printJobs.status.COMPLETED ?? 0} concluídos`}
          empty="Nenhum trabalho de impressão registrado ainda."
          action={
            <Link to="/impressao" className="text-[10px] font-semibold text-primary">
              Abrir fila
            </Link>
          }
          items={data.printJobs.recent}
          renderItem={(item) => (
            <div key={item.id} className="rounded-[18px] border border-surface-container-high bg-surface-container-low px-3.5 py-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] font-semibold leading-5 text-on-surface">{item.name}</div>
                  <div className="mt-0.5 text-[10px] leading-5 text-on-surface-variant">
                    {item.template?.name || 'Sem etiqueta'} • {item.printerName || 'Sem impressora'}
                  </div>
                </div>
                <span className="rounded-full bg-surface px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">
                  {item.status}
                </span>
              </div>
              <div className="mt-1.5 text-[10px] leading-5 text-on-surface-variant">Criado em {formatDate(item.createdAt)}</div>
            </div>
          )}
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <CollectionCard
          title="Formatos cadastrados"
          badge={`${data.labelFormats.total} ativos`}
          empty="Cadastre um formato para separar mídia, sensor e hardware do layout."
          action={
            <Link to="/formatos" className="text-[10px] font-semibold text-primary">
              Gerenciar
            </Link>
          }
          items={data.labelFormats.recent}
          renderItem={(item) => (
            <div key={item.id} className="flex items-center justify-between rounded-[18px] border border-surface-container-high bg-surface-container-low px-3.5 py-3">
              <div>
                <div className="text-[10px] font-semibold leading-5 text-on-surface">{item.name}</div>
                <div className="mt-0.5 text-[10px] leading-5 text-on-surface-variant">
                  {item.widthMm} x {item.heightMm} mm • {item.columns} coluna(s) • {item.mediaType}
                </div>
              </div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">{formatDate(item.updatedAt)}</div>
            </div>
          )}
        />

        <CollectionCard
          title="Usuários por perfil"
          badge={`${Object.values(data.users.byRole).reduce((total, count) => total + count, 0)} perfis`}
          empty="Nenhum usuário encontrado."
          items={Object.entries(data.users.byRole)}
          renderItem={([role, count]) => (
            <div key={role} className="flex items-center justify-between rounded-[18px] border border-surface-container-high bg-surface-container-low px-3.5 py-3">
              <span className="text-[10px] capitalize text-on-surface-variant">{role.toLowerCase()}</span>
              <span className="text-[10px] font-semibold text-on-surface">{count}</span>
            </div>
          )}
        />
      </div>
    </div>
  );
}


